import { localeService, SupportedCurrency } from '../../modules/core';
// eslint-disable-next-line prefer-regex-literals
export const currencyStringRegex = new RegExp('(?<amount>\\d+)(?<currency>[A-Z]+)', 'iu');
const UNKNOWN_DISCOUNT = {
    type: 'unknown',
    amount: 0,
    currency: null,
};
/**
 * Parse discount string; they either come as percentages
 * or as absolute amounts like "15USD" aka $15.00, or 1000GBP
 * aka £1,000.00
 */
export function parseDiscountString(discount) {
    if (discount.endsWith('%')) {
        return {
            type: 'percentage',
            // Divide by 100 to have the value normalised to 0-1
            amount: Number(discount.slice(0, -1)) / 100,
            currency: null,
        };
    }
    if (currencyStringRegex.test(discount)) {
        const result = currencyStringRegex.exec(discount);
        return result.groups.currency.toLowerCase() in SupportedCurrency
            ? {
                type: 'fixed',
                // Multiply by 100 to have the value in pence
                amount: Number(result.groups.amount) * 100,
                currency: result.groups.currency,
            }
            : UNKNOWN_DISCOUNT;
    }
    return UNKNOWN_DISCOUNT;
}
/**
 * A discount is either a relative percentage (expressed 0-100) or an absolute
 * amount. Absolute amounts must be qualified with the currency they are
 * discounting.
 *
 * They are passed from the backend as a string like "20%" or "15USD". This
 * class parses those strings into an object
 */
export class Discount {
    /**
     * @param discountString string - Discount strings like "20%" or "15USD"
     */
    constructor(discountString) {
        const parsed = parseDiscountString(discountString);
        this.type = parsed.type;
        this.amount = parsed.amount;
        this.currency = parsed.currency;
    }
    /**
     * Given a discount format it into an appropriate string
     */
    toString(options) {
        if (this.type === 'percentage') {
            return localeService.instance.formatNumber(this.amount, {
                style: 'percent',
                minimumFractionDigits: options?.showDecimals ? 2 : 0,
                maximumFractionDigits: options?.showDecimals ? 2 : 0,
            });
        }
        if (this.type === 'fixed') {
            return localeService.instance.formatNumber(this.amount / 100, {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: options?.showDecimals ? 2 : 0,
                maximumFractionDigits: options?.showDecimals ? 2 : 0,
            });
        }
        return '';
    }
    /**
     * Given an amount of any currency apply the discount and return the total. Note this
     * method _assumes_ that the amount given and the discount are in the
     * same currency, it also assumes that the amount is in the lowest currency unit
     * i.e. cents or pence, £1.00 would be represented as 100.
     */
    apply(givenAmount) {
        if (this.type === 'percentage') {
            return givenAmount - givenAmount * this.amount;
        }
        if (this.type === 'fixed') {
            return givenAmount - this.amount;
        }
        return givenAmount;
    }
}
